class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.opener = null;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (!overlay) return;

    if (overlay.dataset.cartDrawerBound === 'true') {
      return;
    }

    overlay.dataset.cartDrawerBound = 'true';

    overlay.addEventListener(
      'click',
      this.close.bind(this)
    );
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    if (cartLink.dataset.cartDrawerBound === 'true') {
      return;
    }

    cartLink.dataset.cartDrawerBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (
        event.code === 'Space' ||
        event.code === 'Enter'
      ) {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setActiveElement(element) {
    this.opener = element;
  }

  hasCartItems() {
    return (
      this.querySelector('.cart-item') !== null ||
      this.querySelector('.outlaws-cart-item') !== null
    );
  }

  syncEmptyState() {
    const hasItems = this.hasCartItems();

    const emptyContainer =
      this.querySelector('.drawer__inner-empty');

    /*
     * If product HTML exists, cart is definitely not empty.
     */
    const isEmpty =
      !hasItems && !!emptyContainer;

    this.classList.toggle(
      'is-empty',
      isEmpty
    );

    const drawerItems =
      this.querySelector('cart-drawer-items');

    if (drawerItems) {
      drawerItems.classList.toggle(
        'is-empty',
        isEmpty
      );
    }

    const drawerInner =
      this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.toggle(
        'is-empty',
        isEmpty
      );
    }

    return isEmpty;
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    /*
     * CRITICAL:
     * fix stale is-empty before opening.
     */
    const hasItems = this.hasCartItems();

    if (hasItems) {
      this.classList.remove('is-empty');

      this.querySelector('cart-drawer-items')
        ?.classList.remove('is-empty');

      this.querySelector('.drawer__inner')
        ?.classList.remove('is-empty');
    }

    const cartDrawerNote =
      this.querySelector('[id^="Details-"] summary');

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote
      );
    }

    requestAnimationFrame(() => {
      this.classList.add(
        'animate',
        'active'
      );

      document.body.classList.add(
        'overflow-hidden'
      );
    });

    this.addEventListener(
      'transitionend',
      () => {
        /*
         * Re-check after animation.
         */
        const currentlyHasItems =
          this.hasCartItems();

        if (currentlyHasItems) {
          this.classList.remove(
            'is-empty'
          );

          this.querySelector(
            'cart-drawer-items'
          )?.classList.remove(
            'is-empty'
          );

          this.querySelector(
            '.drawer__inner'
          )?.classList.remove(
            'is-empty'
          );
        }

        const emptyContainer =
          this.querySelector(
            '.drawer__inner-empty'
          );

        const normalContainer =
          this.querySelector(
            '#CartDrawer'
          ) ||
          document.getElementById(
            'CartDrawer'
          );

        /*
         * NEVER pass null to trapFocus.
         */
        const containerToTrapFocusOn =
          this.classList.contains(
            'is-empty'
          ) &&
          emptyContainer
            ? emptyContainer
            : normalContainer;

        const focusElement =
          this.querySelector(
            '.drawer__inner'
          ) ||
          this.querySelector(
            '.drawer__close'
          );

        if (
          containerToTrapFocusOn &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          try {
            trapFocus(
              containerToTrapFocusOn,
              focusElement
            );
          } catch (error) {
            console.warn(
              'CartDrawer focus skipped:',
              error
            );
          }
        }
      },
      {
        once: true
      }
    );

    const drawerItems =
      this.querySelector(
        'cart-drawer-items'
      );

    if (
      drawerItems &&
      typeof drawerItems.dispatchViewEvent ===
        'function'
    ) {
      try {
        drawerItems.dispatchViewEvent();
      } catch (error) {
        console.warn(
          'CartDrawer view event skipped:',
          error
        );
      }
    }
  }

  close() {
    this.classList.remove(
      'active'
    );

    document.body.classList.remove(
      'overflow-hidden'
    );

    if (
      typeof removeTrapFocus ===
        'function'
    ) {
      try {
        removeTrapFocus(
          this.opener
        );
      } catch (error) {
        console.warn(
          'CartDrawer focus restore skipped:',
          error
        );
      }
    } else if (
      this.opener &&
      typeof this.opener.focus ===
        'function'
    ) {
      try {
        this.opener.focus();
      } catch (error) {
        /* ignore */
      }
    }
  }

  setSummaryAccessibility(summary) {
    if (!summary) return;

    summary.setAttribute(
      'role',
      'button'
    );

    summary.setAttribute(
      'aria-expanded',
      summary.parentElement?.hasAttribute(
        'open'
      )
        ? 'true'
        : 'false'
    );

    const nextElement =
      summary.nextElementSibling;

    if (
      nextElement &&
      nextElement.id
    ) {
      summary.setAttribute(
        'aria-controls',
        nextElement.id
      );
    }

    if (
      summary.dataset.cartDrawerBound ===
      'true'
    ) {
      return;
    }

    summary.dataset.cartDrawerBound =
      'true';

    summary.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest(
            'details'
          );

        setTimeout(() => {
          event.currentTarget.setAttribute(
            'aria-expanded',
            details?.hasAttribute('open')
              ? 'true'
              : 'false'
          );
        });
      }
    );

    if (
      summary.parentElement &&
      typeof onKeyUpEscape ===
        'function'
    ) {
      summary.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  renderContents(parsedState) {
    if (!parsedState) {
      console.error(
        'CartDrawer: missing parsedState'
      );

      return;
    }

    if (!parsedState.sections) {
      console.error(
        'CartDrawer: missing sections',
        parsedState
      );

      return;
    }

    this.productId =
      parsedState.id;

    /*
     * The product was just added.
     * Immediately remove stale empty state.
     */
    if (parsedState.id) {
      this.classList.remove(
        'is-empty'
      );

      this.querySelector(
        'cart-drawer-items'
      )?.classList.remove(
        'is-empty'
      );

      this.querySelector(
        '.drawer__inner'
      )?.classList.remove(
        'is-empty'
      );
    }

    this.getSectionsToRender().forEach(
      (section) => {
        const sectionHTML =
          parsedState.sections[
            section.id
          ];

        if (!sectionHTML) {
          console.warn(
            `CartDrawer: section "${section.id}" not returned`
          );

          return;
        }

        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector
              )
            : document.getElementById(
                section.id
              );

        if (!sectionElement) {
          console.warn(
            `CartDrawer: element missing for ${section.id}`
          );

          return;
        }

        const newHTML =
          this.getSectionInnerHTML(
            sectionHTML,
            section.selector
          );

        if (newHTML === null) {
          console.warn(
            `CartDrawer: selector missing inside ${section.id}`
          );

          return;
        }

        sectionElement.innerHTML =
          newHTML;
      }
    );

    /*
     * New HTML has now been inserted.
     * Recalculate empty state based on actual cart item HTML.
     */
    const hasItems =
      this.hasCartItems();

    if (hasItems) {
      this.classList.remove(
        'is-empty'
      );

      this.querySelector(
        'cart-drawer-items'
      )?.classList.remove(
        'is-empty'
      );

      this.querySelector(
        '.drawer__inner'
      )?.classList.remove(
        'is-empty'
      );
    } else {
      this.syncEmptyState();
    }

    /*
     * HTML replacement creates a new overlay.
     */
    this.bindOverlay();

    const summary =
      this.querySelector(
        '[id^="Details-"] summary'
      );

    if (summary) {
      this.setSummaryAccessibility(
        summary
      );
    }

    /*
     * Open only after fresh HTML is fully installed.
     */
    requestAnimationFrame(() => {
      setTimeout(() => {
        /*
         * Final safety check.
         */
        if (this.hasCartItems()) {
          this.classList.remove(
            'is-empty'
          );

          this.querySelector(
            'cart-drawer-items'
          )?.classList.remove(
            'is-empty'
          );

          this.querySelector(
            '.drawer__inner'
          )?.classList.remove(
            'is-empty'
          );
        }

        this.open();

        /*
         * One last async cleanup because Dawn CSS
         * may evaluate is-empty during the opening frame.
         */
        setTimeout(() => {
          if (this.hasCartItems()) {
            this.classList.remove(
              'is-empty'
            );

            this.querySelector(
              'cart-drawer-items'
            )?.classList.remove(
              'is-empty'
            );

            this.querySelector(
              '.drawer__inner'
            )?.classList.remove(
              'is-empty'
            );
          }
        }, 80);

      }, 20);
    });
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) {
      return null;
    }

    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html'
      );

    const element =
      parsedDocument.querySelector(
        selector
      );

    return element
      ? element.innerHTML
      : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section'
  ) {
    if (!html) {
      return null;
    }

    return new DOMParser()
      .parseFromString(
        html,
        'text/html'
      )
      .querySelector(
        selector
      );
  }

  restoreFocusTrap() {
    const cartDrawer =
      this.opener?.closest(
        'cart-drawer.active'
      );

    const cartNotification =
      this.opener
        ?.closest(
          'cart-notification'
        )
        ?.querySelector(
          '#cart-notification.active'
        );

    if (
      cartDrawer &&
      typeof trapFocus === 'function'
    ) {
      const hasItems =
        cartDrawer.querySelector(
          '.cart-item'
        ) !== null;

      if (hasItems) {
        cartDrawer.classList.remove(
          'is-empty'
        );
      }

      const emptyContainer =
        cartDrawer.querySelector(
          '.drawer__inner-empty'
        );

      const normalContainer =
        cartDrawer.querySelector(
          '#CartDrawer'
        ) ||
        document.getElementById(
          'CartDrawer'
        );

      const container =
        cartDrawer.classList.contains(
          'is-empty'
        ) &&
        emptyContainer
          ? emptyContainer
          : normalContainer;

      if (container) {
        try {
          trapFocus(
            container,
            this.opener
          );

          return;
        } catch (error) {
          console.warn(
            'CartDrawer restoreFocusTrap skipped:',
            error
          );
        }
      }
    }

    if (
      cartNotification &&
      typeof trapFocus === 'function'
    ) {
      try {
        trapFocus(
          cartNotification,
          this.opener
        );

        return;
      } catch (error) {
        console.warn(
          'CartNotification focus skipped:',
          error
        );
      }
    }

    if (
      typeof removeTrapFocus ===
        'function'
    ) {
      try {
        removeTrapFocus(
          this.opener
        );
      } catch (error) {
        /* ignore */
      }
    }
  }
}


/* =========================================================
   REGISTER CART DRAWER
========================================================= */

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}


/* =========================================================
   CART DRAWER ITEMS
========================================================= */

if (
  typeof CartItems !==
  'undefined'
) {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner'
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section'
        }
      ];
    }
  }

  if (
    !customElements.get(
      'cart-drawer-items'
    )
  ) {
    customElements.define(
      'cart-drawer-items',
      CartDrawerItems
    );
  }
}